"use client";

import { useState, useEffect } from "react";
import { getProducts } from "@/lib/api/dutchie";
import ProductCard from "@/components/shop/product-card";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { 
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ChevronLeft, ChevronRight, FilterIcon, X } from "lucide-react";
import { cleanUpFilters } from "@/lib/utils";
import type { ProductDisplay } from "@/lib/api/dutchie";
import { useListingScrollRestore, initialPageFromUrl } from "@/lib/hooks/use-listing-scroll";

// Simple type for filter categories
type CategoryFilters = {
  mainCategory: Record<string, boolean>;
  weights: Record<string, boolean>;
  strainTypes: Record<string, boolean>;
  effects: Record<string, boolean>;
  brands: Record<string, boolean>;
  subcategories: Record<string, boolean>;
  potency: {
    thc: { min: number; max: number; value: [number, number]; isAdjusted?: boolean; };
    cbd: { min: number; max: number; value: [number, number]; isAdjusted?: boolean; };
  };
};

// Define a hardcoded weight order for proper numerical sorting
const weightSortOrder = [
  '.5g', '.6g', '.7g', '.75g', '1g', '1.3g', '1.4g', '1.5g',
  '2g', '2.5g', '3g', '3.5g', '3.75g', '5g', '7g', '14g', '28g'
];

// Define common categories
const mainCategories = [
  "Flower", "Pre-Rolls", "Edibles", "Vaporizers", "Concentrates",
  "Topicals", "Tinctures", "Accessories", "Apparel"
];

// Common effects across all products
const commonEffects = [
  "Relaxed", "Happy", "Euphoric", "Uplifted", "Focused", "Creative", "Energetic",
  "Sleepy", "Relief", "Calming", "Healing", "Balanced", "Hungry", "Talkative",
  // Topical effects
  "Cooling", "Warming", "Soothing", "Anti-inflammatory", "Targeted",
  "Local", "Recovery", "Deep Relief", "Long-lasting", "Therapeutic"
];

// Modular Filter Button Component
const FilterButton = ({ isActive, onClick, children, className = '' }: { isActive: boolean; onClick: () => void; children: React.ReactNode; className?: string }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
      isActive
        ? "bg-gradient-to-r from-amber-600 to-amber-500 text-white shadow-sm"
        : "bg-white text-gray-700 hover:bg-gray-50 hover:shadow-sm border border-gray-200"
    } ${className}`}
  >
    {children}
  </button>
);

export default function AllProductsPage() {
  const [products, setProducts] = useState<ProductDisplay[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<ProductDisplay[]>([]);
  const [filters, setFilters] = useState<CategoryFilters>({
    mainCategory: {},
    weights: {},
    strainTypes: {},
    effects: {},
    brands: {},
    subcategories: {},
    potency: {
      thc: { min: 0, max: 100, value: [0, 100], isAdjusted: false },
      cbd: { min: 0, max: 100, value: [0, 100], isAdjusted: false }
    }
  });

  // Pagination state (initial page read from ?page= for back-nav restore)
  const [currentPage, setCurrentPage] = useState(initialPageFromUrl);
  const productsPerPage = 16; // Increased from 12 to 16 for a cleaner grid
  const [loading, setLoading] = useState(true);

  // Restore scroll + page when returning from a product detail page.
  useListingScrollRestore(!loading, currentPage);
  const [sort, setSort] = useState<string>("featured");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Calculate active filter count (only counting user-selected visible filters)
  const activeFilterCount = () => {
    let count = 0;

    // Count only filters that have been explicitly set to true
    count += Object.values(filters.weights).filter(Boolean).length;
    count += Object.values(filters.strainTypes).filter(Boolean).length;
    count += Object.values(filters.effects).filter(Boolean).length;
    count += Object.values(filters.brands).filter(Boolean).length;

    // Count potency filters only if they've been adjusted
    if (filters.potency.thc.isAdjusted) count += 1;
    if (filters.potency.cbd.isAdjusted) count += 1;

    return count;
  };

  // Fetch products
  useEffect(() => {
    async function fetchProducts() {
      try {
        // Set loading state
        setLoading(true);
        console.log('Fetching products...');
        
        // Fetch all products
        const fetchedProducts = await getProducts();
        console.log(`Fetched ${fetchedProducts.length} total products`);
        
        // Filter for in-stock products only
        const inStockProducts = fetchedProducts.filter((p: ProductDisplay) => p.inStock);
        console.log(`${inStockProducts.length} products are in stock`);

        // De-duplicate products based on ID
        const uniqueProductsMap = new Map<string, ProductDisplay>();
        inStockProducts.forEach((product: ProductDisplay) => {
          if (!uniqueProductsMap.has(product.id)) {
            uniqueProductsMap.set(product.id, product);
          }
        });

        const uniqueProducts = Array.from(uniqueProductsMap.values());
        console.log(`${uniqueProducts.length} unique products after deduplication`);

        // Enhance products with normalized data for better filtering
        const enhancedProducts = enhanceProductData(uniqueProducts);
        
        // Log some product categories to help with debugging
        const categories = new Set();
        enhancedProducts.forEach(p => {
          if (p.category) {
            categories.add(p.category.replace(/_/g, ' '));
          }
        });
        console.log('Found product categories:', Array.from(categories));
        
        // Set products in state
        setProducts(enhancedProducts);
        setFilteredProducts(enhancedProducts);

        // Extract filters from products
        const extractedFilters = extractFilters(enhancedProducts);
        setFilters(extractedFilters);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching all products:", error);
        setLoading(false);
      }
    }
    fetchProducts();
  }, []);

  // Add derived properties to products for better filtering
  function enhanceProductData(products: ProductDisplay[]): ProductDisplay[] {
    return products.map(product => {
      // Extract THC/CBD values from various sources
      const thcValue = extractPotencyValue(product, 'thc');
      const cbdValue = extractPotencyValue(product, 'cbd');

      // Normalize weight for proper filtering
      const normalizedWeight = normalizeWeight(product.option || '');

      // Check if product is full spectrum
      const isFullSpectrum = Boolean(
        product.name && 
        /full.?spectrum/i.test(product.name + ' ' + (product.description || ''))
      );
      
      // Check if product is CBD-dominant
      const isCBDDominant = Boolean(
        (cbdValue > thcValue * 2) && cbdValue > 0
      );
      
      // Check if premium product (over $80)
      const isPremium = (product.price || 0) > 80;

      // Parse effects from product description
      const parsedEffects = parseEffectsFromText(product);

      return {
        ...product,
        thcContent: thcValue,
        cbdContent: cbdValue,
        normalizedWeight,
        isFullSpectrum,
        isCBDDominant,
        isPremium,
        effects: [...(product.effects || []), ...parsedEffects]
      };
    });
  }

  // Extract potency value from product
  function extractPotencyValue(product: ProductDisplay, type: 'thc' | 'cbd'): number {
    // First try direct fields
    const directValue = type === 'thc' ? product.thc : product.cbd;
    if (directValue) {
      const match = directValue.match(/(\d+(?:\.\d+)?)/);
      if (match && match[1]) {
        const value = parseFloat(match[1]);
        // Ensure reasonable range (0-100%)
        return value > 100 ? value / 10 : value;
      }
    }

    // Then try potency objects
    const potencyObj = type === 'thc' ? (product as any).potencyThc : (product as any).potencyCbd;
    if (potencyObj && potencyObj.formatted) {
      const match = potencyObj.formatted.match(/(\d+(?:\.\d+)?)/);
      if (match && match[1]) {
        const value = parseFloat(match[1]);
        // Ensure reasonable range (0-100%)
        return value > 100 ? value / 10 : value;
      }
    }

    // Finally try to extract from text
    const productText = `${product.name || ''} ${product.description || ''}`.toLowerCase();
    const regex = new RegExp(`${type}:?\s*(\d+(?:\.\d+)?)\s*%`, 'i');
    const match = productText.match(regex);

    if (match && match[1]) {
      const value = parseFloat(match[1]);
      // Ensure reasonable range (0-100%)
      return value > 100 ? value / 10 : value;
    }

    return 0; // Default to 0 if no value found
  }

  // Normalize weight format
  function normalizeWeight(weight: string): string {
    if (!weight) return '';

    // Convert to lowercase and remove spaces
    const cleaned = weight.toLowerCase().trim();

    // Already in a standard format
    if (weightSortOrder.includes(cleaned)) {
      return cleaned;
    }

    // Handle special case formatting
    if (cleaned === '1 gram' || cleaned === '1gram' || cleaned === '1.0g') return '1g';
    if (cleaned === '2 gram' || cleaned === '2gram' || cleaned === '2.0g') return '2g';
    if (cleaned === '3.5 gram' || cleaned === '3.5gram' || cleaned === '3.5 g') return '3.5g';
    if (cleaned === '7 gram' || cleaned === '7gram' || cleaned === '7.0g') return '7g';
    if (cleaned === '14 gram' || cleaned === '14gram' || cleaned === '14.0g') return '14g';
    if (cleaned === '28 gram' || cleaned === '28gram' || cleaned === '28.0g') return '28g';
    if (cleaned.includes('eighth') || cleaned.includes('1/8')) return '3.5g';
    if (cleaned.includes('quarter') || cleaned.includes('1/4')) return '7g';
    if (cleaned.includes('half') || cleaned.includes('1/2')) return '14g';
    if (cleaned.includes('ounce') || cleaned.includes('oz')) return '28g';

    // Extract numeric portion and units
    const match = cleaned.match(/(\d+(?:\.\d+)?)\s*(g|gram|grams|oz|ounce|ounces|eighth|quarter|half|lb|pound)?/i);
    if (!match) return weight;

    const value = parseFloat(match[1]);
    const unit = (match[2] || 'g').toLowerCase();

    // Convert to standard format based on unit
    if (unit.includes('eighth')) return '3.5g';
    if (unit.includes('quarter')) return '7g';
    if (unit.includes('half')) return '14g';
    if (unit.includes('oz') || unit.includes('ounce')) return '28g';
    if (unit.includes('lb') || unit.includes('pound')) return (value * 448) + 'g';

    // Default to grams
    return value + 'g';
  }

  // Function to filter out options that don't match any products
  function filterNonMatchingOptions(options: Record<string, boolean>, products: ProductDisplay[], filterType: string): Record<string, boolean> {
    const result: Record<string, boolean> = {};
    
    // Skip processing if no products
    if (products.length === 0) {
      return {}; // Return empty object if no products
    }
    
    // For each filter option, check if at least one product would match it
    Object.keys(options).forEach(option => {
      // Uncomment to debug filter options
      // console.log(`Testing filter option: ${filterType} - ${option}`);
      
      // Create a test filter state with just this option enabled
      const testFilter: Record<string, boolean> = { [option]: true };
      
      // Check if any product would match this filter option
      const hasMatchingProducts = products.some(product => {
        // For main category filter
        if (filterType === 'category') {
          let productCategory = '';
          
          if (product.category) {
            productCategory = product.category
              .replace(/_/g, ' ')
              .split(' ')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
              .join(' ');
          }
          
          // Check product category against the option
          const singularOption = option.toLowerCase().endsWith('s') ? option.toLowerCase().slice(0, -1) : option.toLowerCase();
          const singularProduct = productCategory.toLowerCase().endsWith('s') ? 
            productCategory.toLowerCase().slice(0, -1) : productCategory.toLowerCase();
            
          return productCategory.toLowerCase() === option.toLowerCase() || 
                singularProduct === singularOption || 
                productCategory.toLowerCase().includes(singularOption);
        }
        
        // For weight filter
        else if (filterType === 'weights') {
          const normalizedProductWeight = product.option ? normalizeWeight(product.option) : '';
          
          // Check option field first with normalized weight
          let weightMatch = normalizedProductWeight.toLowerCase() === option.toLowerCase();
          
          // If no match and product has variants, check variants with normalized weights
          if (!weightMatch && product.variants && product.variants.length > 0) {
            weightMatch = product.variants.some((variant: any) => {
              const normalizedVariantWeight = variant.option ? normalizeWeight(variant.option) : '';
              return normalizedVariantWeight.toLowerCase() === option.toLowerCase();
            });
          }
          
          return weightMatch;
        }
        
        // For strain types
        else if (filterType === 'types') {
          const lowerStrainType = (product.strainType || '').toLowerCase();
          let normalizedType = '';
          
          // Determine the normalized strain type
          if (lowerStrainType.includes('sativa') && lowerStrainType.includes('hybrid')) {
            normalizedType = 'sativa hybrid';
          } else if (lowerStrainType.includes('indica') && lowerStrainType.includes('hybrid')) {
            normalizedType = 'indica hybrid';
          } else if (lowerStrainType.includes('hybrid')) {
            normalizedType = 'hybrid';
          } else if (lowerStrainType.includes('sativa')) {
            normalizedType = 'sativa';
          } else if (lowerStrainType.includes('indica')) {
            normalizedType = 'indica';
          }
          
          // Check strain type against the option
          const optionType = option.toLowerCase();
          
          // For 'hybrid' selection, only match pure hybrids
          if (optionType === 'hybrid') {
            return normalizedType === 'hybrid';
          }
          // For specific hybrid variations, exact match
          else if (optionType === 'sativa hybrid' || optionType === 'indica hybrid') {
            return optionType === normalizedType;
          }
          // For pure sativa/indica, exact match
          else if (optionType === 'sativa' || optionType === 'indica') {
            return optionType === normalizedType;
          }
          // Fallback for any other cases
          return normalizedType.includes(optionType);
        }
        
        // For effects
        else if (filterType === 'effects') {
          return product.effects?.some(e => 
            e.toLowerCase().includes(option.toLowerCase())
          );
        }
        
        // For brands
        else if (filterType === 'brand') {
          return product.brand === option;
        }
        
        // For subcategories
        else if (filterType === 'subcategory') {
          // Full Spectrum is a special case
          if (option === 'Full Spectrum') {
            return (product as any).isFullSpectrum;
          }
          
          // CBD Dominant is a special case
          else if (option === 'CBD Dominant') {
            return (product as any).isCBDDominant;
          }
          
          // Premium is a special case
          else if (option === 'Premium') {
            return (product as any).isPremium;
          }
          
          // For general subcategories
          else if (product.subcategory) {
            return product.subcategory.replace(/_/g, ' ').toUpperCase() === option.toUpperCase();
          }
          
          // For inferred subcategories
          else {
            const productText = (product.name + ' ' + (product.description || '')).toLowerCase();
            
            if (option.toLowerCase() === 'balm') return productText.includes('balm');
            if (option.toLowerCase() === 'lotion') return productText.includes('lotion');
            if (option.toLowerCase() === 'cream') return productText.includes('cream');
            if (option.toLowerCase() === 'salve') return productText.includes('salve');
            if (option.toLowerCase() === 'roll-on') return productText.includes('roll-on') || productText.includes('roll on');
            if (option.toLowerCase() === 'transdermal') return productText.includes('transdermal');
          }
        }
        
        return false; // Default fallback
      });
      
      // Only include options that have matching products
      if (hasMatchingProducts) {
        result[option] = options[option];
      }
    });
    
    return result;
  }
  
  // Check if product would be good for beginners
  const isTargetedRelief = (product: ProductDisplay): boolean => {
    // Check if the product provides targeted relief for specific areas
    const text = `${product.name} ${product.description}`.toLowerCase();
    return /targeted|specific|localized|joint|muscle|area|spot/i.test(text);
  }

  // Parse effects from product description
  function parseEffectsFromText(product: ProductDisplay): string[] {
    // Use both name and description for effect detection, but filter out type-related terms
    const normalizedText = `${product.name || ''} ${product.description || ''}`.toLowerCase()
      .replace(/_/g, ' ')
      .replace(/-/g, ' ');
    
    const foundEffects: string[] = [];
    
    // These aren't true effects, they're product types, so exclude them from effects list
    const excludedEffects = ['single', 'singles', 'pack', 'packs', 'infused', 'premium'];

    // Check for all legitimate effects in the description
    commonEffects.forEach(effect => {
      // Skip product types that aren't true effects
      if (!excludedEffects.includes(effect.toLowerCase())) {
        const effectPattern = effect.toLowerCase().replace(/\s+/g, '[\\s_-]+');
        const regex = new RegExp(`\\b${effectPattern}\\b`, 'i');
        
        if (regex.test(normalizedText)) {
          foundEffects.push(effect);
        }
      }
    });

    return foundEffects;
  }

  // Extract filters from products
  function extractFilters(products: ProductDisplay[]): CategoryFilters {
    const mainCategory: Record<string, boolean> = {};
    const weights: Record<string, boolean> = {};
    const strainTypes: Record<string, boolean> = {};
    const effects: Record<string, boolean> = {};
    const brands: Record<string, boolean> = {};
    const subcategories: Record<string, boolean> = {};

    // Track THC/CBD ranges
    let thcMin = 100;
    let thcMax = 0;
    let cbdMin = 100;
    let cbdMax = 0;

    // Flag to check if we found any valid THC/CBD values - important for topicals which may not always list potency
    let foundThc = false;
    let foundCbd = false;

    // Define valid strain types - include hybrid variations
    const validStrainTypes = ['Sativa', 'Indica', 'Hybrid', 'Sativa Hybrid', 'Indica Hybrid'];

    products.forEach(product => {
      // Extract weight
      if (product.option && product.option !== 'N/A') {
        weights[product.option] = false;
      } else if (product.variants && product.variants.length > 0) {
        // Extract weight from first variant if no main option is available
        product.variants.forEach(variant => {
          if (variant.option && variant.option !== 'N/A') {
            weights[variant.option] = false;
          }
        });
      }

      // Extract strain type with hybrid variation detection
      if (product.strainType && product.strainType.toLowerCase() !== 'default') {
        let normalizedStrainType = product.strainType
          .replace(/_/g, ' ')
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ');

        // Detect Sativa-dominant and Indica-dominant hybrids
        const lowerStrainType = normalizedStrainType.toLowerCase();
        if (lowerStrainType.includes('sativa') && lowerStrainType.includes('hybrid')) {
          normalizedStrainType = 'Sativa Hybrid';
        } else if (lowerStrainType.includes('indica') && lowerStrainType.includes('hybrid')) {
          normalizedStrainType = 'Indica Hybrid';
        } else if (lowerStrainType.includes('hybrid')) {
          normalizedStrainType = 'Hybrid';
        } else if (lowerStrainType.includes('sativa')) {
          normalizedStrainType = 'Sativa';
        } else if (lowerStrainType.includes('indica')) {
          normalizedStrainType = 'Indica';
        }

        // Add the normalized strain type to our filters
        if (validStrainTypes.includes(normalizedStrainType)) {
          strainTypes[normalizedStrainType] = false;
        }
      }

      // Extract effects
      if (product.effects && product.effects.length > 0) {
        product.effects.forEach(effect => {
          // Replace underscores with spaces and capitalize each word
          const normalizedEffect = effect
            .replace(/_/g, ' ')
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
          effects[normalizedEffect] = false;
        });
      }

      // Extract brand
      if (product.brand) {
        brands[product.brand] = false;
      }

      // Extract subcategory
      if (product.subcategory && product.subcategory.toUpperCase() !== 'DEFAULT') {
        // Format subcategory names for display
        const formattedSubcategory = product.subcategory
          .replace(/_/g, ' ')
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ');

        subcategories[formattedSubcategory] = false;
      }

      // Add common topical types as subcategories based on product name and description
      const text = (product.name + ' ' + (product.description || '')).toLowerCase();
      if (text.includes('balm')) subcategories['Balm'] = false;
      if (text.includes('lotion')) subcategories['Lotion'] = false;
      if (text.includes('cream')) subcategories['Cream'] = false;
      if (text.includes('salve')) subcategories['Salve'] = false;
      if (text.includes('roll-on') || text.includes('roll on')) subcategories['Roll-on'] = false;
      if (text.includes('transdermal')) subcategories['Transdermal'] = false;
      if (text.includes('full spectrum')) subcategories['Full Spectrum'] = false;

      // Add CBD Dominant as a subcategory based on potency
      const thcValue = extractPotencyValue(product, 'thc');
      const cbdValue = extractPotencyValue(product, 'cbd');
      if (cbdValue > thcValue * 2 && cbdValue > 0) {
        subcategories['CBD Dominant'] = false;
      }
      
      // Add THC Dominant as a subcategory based on potency
      if (thcValue > cbdValue * 2 && thcValue > 0) {
        subcategories['THC Dominant'] = false;
      }
      
      // Add category as a filter option
      if (product.category) {
        // Format category name properly
        const formattedCategory = product.category
          .replace(/_/g, ' ')
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ');
        
        // Add to main categories if it's a recognized type
        if (mainCategories.includes(formattedCategory)) {
          mainCategory[formattedCategory] = false;
        }
      }

      // Track THC/CBD ranges
      const thc = product.thc !== undefined ? product.thc : (product as any).thc;
      if (thc && !isNaN(thc)) {
        thcMin = Math.min(thcMin, thc);
        thcMax = Math.max(thcMax, thc);
        foundThc = true;
      }

      const cbd = product.cbd !== undefined ? product.cbd : (product as any).cbd;
      if (cbd && !isNaN(cbd)) {
        cbdMin = Math.min(cbdMin, cbd);
        cbdMax = Math.max(cbdMax, cbd);
        foundCbd = true;
      }
    });

    // Properly filter out options that don't match any products
    const cleanedMainCategory = filterNonMatchingOptions(mainCategory, products, 'category');
    const cleanedWeights = filterNonMatchingOptions(weights, products, 'weights');
    const cleanedStrainTypes = filterNonMatchingOptions(strainTypes, products, 'types');
    const cleanedEffects = filterNonMatchingOptions(effects, products, 'effects');
    const cleanedBrands = filterNonMatchingOptions(brands, products, 'brand');
    const cleanedSubcategories = filterNonMatchingOptions(subcategories, products, 'subcategory');

    // Handle extreme values - ensure potency is within reasonable range
    if (thcMin > 100 || thcMax > 100) {
      thcMin = Math.min(thcMin, 100);
      thcMax = Math.min(thcMax, 100);
    }
    
    if (cbdMin > 100 || cbdMax > 100) {
      cbdMin = Math.min(cbdMin, 100);
      cbdMax = Math.min(cbdMax, 100);
    }

    // Ensure min and max values are whole numbers
    thcMin = Math.floor(thcMin);
    thcMax = Math.ceil(thcMax);
    cbdMin = Math.floor(cbdMin);
    cbdMax = Math.ceil(cbdMax);

    // Ensure potency values are valid
    if (thcMin > thcMax || !foundThc) {
      thcMin = 0;
      thcMax = 30; // Default max for THC - reasonable for most products
    }

    if (cbdMin > cbdMax || !foundCbd) {
      cbdMin = 0;
      cbdMax = 30; // Default max for CBD
    }

    // Round potency values for better UX
    thcMin = Math.floor(thcMin);
    thcMax = Math.ceil(thcMax);
    cbdMin = Math.floor(cbdMin);
    cbdMax = Math.ceil(cbdMax);

    // Ensure we have sensible ranges (minimum 1% range)
    if (thcMax <= thcMin) thcMax = thcMin + 1;
    if (cbdMax <= cbdMin) cbdMax = cbdMin + 1;
    
    // Cap maximum values to prevent UI issues
    thcMax = Math.min(thcMax, 100);
    cbdMax = Math.min(cbdMax, 100);

    // Add all main categories if none were found
    if (Object.keys(cleanedMainCategory).length === 0) {
      mainCategories.forEach(category => {
        cleanedMainCategory[category] = false;
      });
    }

    return {
      mainCategory: cleanedMainCategory,
      weights: cleanedWeights,
      strainTypes: cleanedStrainTypes,
      effects: cleanedEffects,
      brands: cleanedBrands,
      subcategories: cleanedSubcategories,
      potency: {
        thc: { min: Math.floor(thcMin), max: Math.ceil(thcMax), value: [Math.floor(thcMin), Math.ceil(thcMax)], isAdjusted: false },
        cbd: { min: Math.floor(cbdMin), max: Math.ceil(cbdMax), value: [Math.floor(cbdMin), Math.ceil(cbdMax)], isAdjusted: false }
      }
    };
  }

  // Reset pagination when filters or sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, sort, searchQuery]);
  
  // Initialize potency values
  const [thcRange, setThcRange] = useState<{ min: number; max: number; }>({ min: 0, max: 100 });
  const [cbdRange, setCbdRange] = useState<{ min: number; max: number; }>({ min: 0, max: 20 });
  
  // Effect to calculate potency ranges from products
  useEffect(() => {
    if (products.length === 0) return;
    
    let foundThc = false;
    let thcMin = 100;
    let thcMax = 0;
    let cbdMin = 20;
    let cbdMax = 0;
    
    // Calculate THC and CBD ranges from actual product data
    products.forEach(product => {
      const thcValue = extractPotencyValue(product, 'thc');
      const cbdValue = extractPotencyValue(product, 'cbd');
      
      if (thcValue > 0) {
        foundThc = true;
        thcMin = Math.min(thcMin, thcValue);
        thcMax = Math.max(thcMax, thcValue);
      }
      
      if (cbdValue > 0) {
        cbdMin = Math.min(cbdMin, cbdValue);
        cbdMax = Math.max(cbdMax, cbdValue);
      }
    });
    
    // Round potency values for better UX
    thcMin = Math.floor(thcMin);
    thcMax = Math.ceil(thcMax);
    cbdMin = Math.floor(cbdMin);
    cbdMax = Math.ceil(cbdMax);
    
    // Set default values if no valid THC/CBD data was found
    if (!foundThc) {
      thcMin = 0;
      thcMax = 100; // Accommodate high-potency infused pre-rolls
    }
    
    // Update state with calculated ranges
    setThcRange({ min: thcMin, max: thcMax });
    setCbdRange({ min: cbdMin, max: cbdMax });
    
    // Initialize filter potency values to the full range
    setFilters(prev => ({
      ...prev,
      potency: {
        thc: { min: thcMin, max: thcMax, value: [thcMin, thcMax], isAdjusted: false },
        cbd: { min: cbdMin, max: cbdMax, value: [cbdMin, cbdMax], isAdjusted: false }
      }
    }));
  }, [products]);
  // Apply filters to products and update filteredProducts state
  useEffect(() => {
    // Apply all filters to products
    const applyFilters = (product: ProductDisplay): boolean => {
      // Main Category filter
      const selectedMainCategories = Object.entries(filters.mainCategory)
        .filter(([_, isSelected]) => isSelected)
        .map(([category]) => category.toLowerCase());
        
      if (selectedMainCategories.length > 0) {
        // Format the product's category for comparison
        let productCategory = '';
        
        if (product.category) {
          productCategory = product.category
            .replace(/_/g, ' ')
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
        }
        
        // If category is empty or not recognized, try to infer from subcategory or name
        if (!productCategory || productCategory === 'Default') {
          // Check if product name contains category name
          const productText = (product.name + ' ' + (product.description || '')).toLowerCase();
          
          // Try to infer category from product name/description
          if (productText.includes('flower') || productText.includes('bud')) {
            productCategory = 'Flower';
          } else if (productText.includes('edible') || productText.includes('gummy') || productText.includes('chocolate')) {
            productCategory = 'Edibles';
          } else if (productText.includes('vape') || productText.includes('cartridge') || productText.includes('vaporizer')) {
            productCategory = 'Vaporizers';
          } else if (productText.includes('pre-roll') || productText.includes('preroll') || productText.includes('joint')) {
            productCategory = 'Pre-Rolls';
          } else if (productText.includes('concentrate') || productText.includes('wax') || productText.includes('shatter') || productText.includes('rosin')) {
            productCategory = 'Concentrates';
          } else if (productText.includes('topical') || productText.includes('lotion') || productText.includes('balm') || productText.includes('cream')) {
            productCategory = 'Topicals';
          } else if (productText.includes('tincture') || productText.includes('drops')) {
            productCategory = 'Tinctures';
          }
        }
          
        // Check if the product's category matches any of the selected categories
        const categoryMatch = selectedMainCategories.some(cat => {
          // Special case for plurals (Edible vs Edibles, etc.)
          const singularCat = cat.endsWith('s') ? cat.slice(0, -1) : cat;
          const singularProduct = productCategory.toLowerCase().endsWith('s') ? 
            productCategory.toLowerCase().slice(0, -1) : productCategory.toLowerCase();
            
          return productCategory.toLowerCase() === cat || 
                 singularProduct === singularCat || 
                 productCategory.toLowerCase().includes(singularCat);
        });
        
        if (!categoryMatch) {
          return false;
        }
      }
      
      // Weight filter
      const selectedWeights = Object.entries(filters.weights)
        .filter(([_, isSelected]) => isSelected)
        .map(([weight]) => weight);
        
      if (selectedWeights.length > 0) {
        // Normalize the product option and variant options for better matching
        const normalizedProductWeight = product.option ? normalizeWeight(product.option) : '';
        
        // Check option field first with normalized weight
        let weightMatch = selectedWeights.some((weight: string) => 
          normalizedProductWeight.toLowerCase() === weight.toLowerCase()
        );

        // If no match and product has variants, check variants with normalized weights
        if (!weightMatch && product.variants && product.variants.length > 0) {
          weightMatch = product.variants.some((variant: any) => {
            const normalizedVariantWeight = variant.option ? normalizeWeight(variant.option) : '';
            return selectedWeights.some((weight: string) => 
              normalizedVariantWeight.toLowerCase() === weight.toLowerCase()
            );
          });
        }

        if (!weightMatch) return false;
      }

      // Strain type filter with special handling for hybrid variations
      const selectedStrainTypes = Object.entries(filters.strainTypes)
        .filter(([_, isSelected]) => isSelected)
        .map(([type]) => type.toLowerCase());

      if (selectedStrainTypes.length > 0) {
        // Get the normalized strain type for this product
        const lowerStrainType = (product.strainType || '').toLowerCase();
        let normalizedType = '';
        
        // Determine the normalized strain type
        if (lowerStrainType.includes('sativa') && lowerStrainType.includes('hybrid')) {
          normalizedType = 'sativa hybrid';
        } else if (lowerStrainType.includes('indica') && lowerStrainType.includes('hybrid')) {
          normalizedType = 'indica hybrid';
        } else if (lowerStrainType.includes('hybrid')) {
          normalizedType = 'hybrid';
        } else if (lowerStrainType.includes('sativa')) {
          normalizedType = 'sativa';
        } else if (lowerStrainType.includes('indica')) {
          normalizedType = 'indica';
        }
        
        // Check if any selected strain type matches the normalized type
        const strainTypeMatch = selectedStrainTypes.some(type => {
          // For 'hybrid' selection, only match pure hybrids
          if (type === 'hybrid') {
            return normalizedType === 'hybrid';
          }
          // For specific hybrid variations, exact match
          else if (type === 'sativa hybrid' || type === 'indica hybrid') {
            return type === normalizedType;
          }
          // For pure sativa/indica, exact match
          else if (type === 'sativa' || type === 'indica') {
            return type === normalizedType;
          }
          // Fallback for any other cases
          return normalizedType.includes(type);
        });
        
        if (!strainTypeMatch) return false;
      }

      // Effects filter
      const selectedEffects = Object.entries(filters.effects)
        .filter(([_, isSelected]) => isSelected)
        .map(([effect]) => effect.toLowerCase());

      if (selectedEffects.length > 0) {
        const effectsMatch = selectedEffects.some(effect =>
          product.effects?.some(e => e.toLowerCase().includes(effect))
        );
        if (!effectsMatch) return false;
      }

      // Brand filter
      const selectedBrands = Object.entries(filters.brands)
        .filter(([_, isSelected]) => isSelected)
        .map(([brand]) => brand);

      if (selectedBrands.length > 0) {
        const brandMatch = selectedBrands.some(brand => product.brand === brand);
        if (!brandMatch) return false;
      }

      // Subcategory filter
      const selectedSubcategories = Object.entries(filters.subcategories)
        .filter(([_, isSelected]) => isSelected)
        .map(([subcategory]) => subcategory);

      if (selectedSubcategories.length > 0) {
        // Each subcategory type is evaluated separately
        // Singles and Packs are special cases - we only need to match one if they're selected
        const singlesSelected = selectedSubcategories.includes('Singles');
        const packsSelected = selectedSubcategories.includes('Packs');
        
        // Check for Full Spectrum subcategory
        if (selectedSubcategories.includes('Full Spectrum') && !(product as any).isFullSpectrum) {
          return false;
        }
        
        // Check for CBD-Dominant subcategory
        if (selectedSubcategories.includes('CBD Dominant') && !(product as any).isCBDDominant) {
          return false;
        }
        
        // Check for Premium subcategory
        if (selectedSubcategories.includes('Premium') && !(product as any).isPremium) {
          return false;
        }
        
        // Check for regular subcategories (not Singles, Packs, Infused, or Premium)
        const otherSubcategories = selectedSubcategories.filter(subcat => 
          !['Singles', 'Packs', 'Infused', 'Premium'].includes(subcat)
        );
        
        if (otherSubcategories.length > 0) {
          const regularSubcategoryMatch = product.subcategory && otherSubcategories.some(subcat =>
            product.subcategory?.replace(/_/g, ' ').toUpperCase() === subcat.toUpperCase()
          );
          
          if (!regularSubcategoryMatch) return false;
        }
      }

      // THC potency filter
      if (filters.potency.thc.isAdjusted) {
        const [minThc, maxThc] = filters.potency.thc.value;
        // Use the enhanced thcContent property we calculated earlier
        const thcValue = (product as any).thcContent;
        
        // Simple range check - exclude if outside min/max range
        if (thcValue !== undefined && !isNaN(thcValue) && (thcValue < minThc || thcValue > maxThc)) {
          return false;
        }
      }

      // CBD potency filter
      if (filters.potency.cbd.isAdjusted) {
        const [minCbd, maxCbd] = filters.potency.cbd.value;
        // Use the enhanced cbdContent property we calculated earlier
        const cbdValue = (product as any).cbdContent;
        
        // Simple range check - exclude if outside min/max range
        if (cbdValue !== undefined && !isNaN(cbdValue) && (cbdValue < minCbd || cbdValue > maxCbd)) {
          return false;
        }
      }

      // Search query filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const nameMatch = product.name?.toLowerCase().includes(query);
        const brandMatch = product.brand?.toLowerCase().includes(query);
        const descMatch = product.description?.toLowerCase().includes(query);

        if (!nameMatch && !brandMatch && !descMatch) return false;
      }

      return true;
    };
    
    // Apply filters and get filtered products
    const filtered = products.filter(applyFilters);
    
    // Sort products
    const sorted = [...filtered].sort((a, b) => {
      if (sort === "price-asc") {
        return (a.price || 0) - (b.price || 0);
      } else if (sort === "price-desc") {
        return (b.price || 0) - (a.price || 0);
      } else if (sort === "potency-desc") {
        // Use the enhanced thcContent property we calculated earlier
        const bThc = (b as any).thcContent || 0;
        const aThc = (a as any).thcContent || 0;
        return bThc - aThc;
      } else if (sort === "name-asc") {
        return (a.name || "").localeCompare(b.name || "");
      } else if (sort === "name-desc") {
        return (b.name || "").localeCompare(a.name || "");
      } else {
        // Default to featured sorting
        return ((b as any).featured ? 1 : 0) - ((a as any).featured ? 1 : 0);
      }
    });

    setFilteredProducts(sorted);
  }, [products, filters, sort, searchQuery]);

  // Toggle a filter value
  const toggleFilter = (
    category: keyof Omit<CategoryFilters, 'potency'>,
    value: string
  ) => {
    setFilters(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [value]: !prev[category][value]
      }
    }));
  };

  // Handle potency slider change
  const handlePotencyChange = (type: 'thc' | 'cbd', value: [number, number]) => {
    setFilters(prev => ({
      ...prev,
      potency: {
        ...prev.potency,
        [type]: {
          ...prev.potency[type],
          value,
          isAdjusted: true
        }
      }
    }));
  };

  // Reset filters
  const resetFilters = () => {
    setFilters(prevFilters => ({
      mainCategory: Object.keys(prevFilters.mainCategory).reduce((acc, key) => ({ ...acc, [key]: false }), {}),
      weights: Object.keys(prevFilters.weights).reduce((acc, key) => ({ ...acc, [key]: false }), {}),
      strainTypes: Object.keys(prevFilters.strainTypes).reduce((acc, key) => ({ ...acc, [key]: false }), {}),
      effects: Object.keys(prevFilters.effects).reduce((acc, key) => ({ ...acc, [key]: false }), {}),
      brands: Object.keys(prevFilters.brands).reduce((acc, key) => ({ ...acc, [key]: false }), {}),
      subcategories: Object.keys(prevFilters.subcategories).reduce((acc, key) => ({ ...acc, [key]: false }), {}),
      potency: {
        thc: {
          ...prevFilters.potency.thc,
          value: [prevFilters.potency.thc.min, prevFilters.potency.thc.max],
          isAdjusted: false
        },
        cbd: {
          ...prevFilters.potency.cbd,
          value: [prevFilters.potency.cbd.min, prevFilters.potency.cbd.max],
          isAdjusted: false
        }
      }
    }));
    setSearchQuery("");
  };

  // Get sorted weight keys based on predefined order
  const getSortedWeightKeys = () => {
    return Object.keys(filters.weights).sort((a, b) => {
      const aIndex = weightSortOrder.indexOf(a.toLowerCase());
      const bIndex = weightSortOrder.indexOf(b.toLowerCase());

      if (aIndex === -1 && bIndex === -1) return a.localeCompare(b);
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;

      return aIndex - bIndex;
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-teal-600 to-teal-800 text-white py-10">
        <div className="container mx-auto px-6">
          <div className="flex items-center mb-4">
            <Link href="/shop" className="flex items-center text-white hover:text-teal-100 transition-colors">
              <ChevronLeft className="h-4 w-4 mr-1" />
              <span className="text-sm">Back to Shop</span>
            </Link>
          </div>

          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold">Shop All Products</h1>
              <p className="mt-2 max-w-2xl">Browse our selection of high-quality cannabis products</p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
              <div className="relative hidden lg:block w-64">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="absolute left-2 top-2.5 h-4 w-4 text-white/60">
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.3-4.3" />
                </svg>
                <input
                  className="flex h-10 w-full rounded-md border px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm pl-8 bg-white/10 border-white/20 text-white placeholder:text-white/60"
                  placeholder="Search All Products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              <Button
                onClick={() => setShowMobileFilters(true)}
                className="bg-white/10 border-white/20 text-white hover:bg-accent hover:text-accent-foreground"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 mr-2">
                  <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                </svg>
                Filters
                <span className="ml-1 w-5 h-5 rounded-full bg-white text-amber-700 text-xs flex items-center justify-center">
                  {activeFilterCount()}
                </span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto py-8 px-4 sm:px-6 lg:px-8">

        <div className="flex items-center justify-between mb-6">
          {/* Mobile search visible only on small screens */}
          <div className="relative md:hidden w-full">
            <input
              type="text"
              placeholder="Search All Products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-4 pr-10 py-2 border border-gray-300 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-amber-600 focus:border-transparent"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Sort options */}
          <div className="flex items-center ml-auto">
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-600 focus:border-transparent"
            >
              <option value="featured">Featured</option>
              <option value="price-asc">Price: Low to High</option>
              <option value="price-desc">Price: High to Low</option>
              <option value="potency-desc">THC %: High to Low</option>
              <option value="name-asc">Name: A to Z</option>
            </select>
          </div>

          {/* Mobile filter button */}
          <Button
            onClick={() => setShowMobileFilters(true)}
            className="md:hidden ml-2 bg-amber-600 hover:bg-amber-700 text-white"
          >
            <FilterIcon className="h-4 w-4 mr-2" />
            Filters
            {activeFilterCount() > 0 && (
              <span className="ml-1 w-5 h-5 rounded-full bg-white text-amber-700 text-xs flex items-center justify-center">
                {activeFilterCount()}
              </span>
            )}
          </Button>
        </div>

        {/* Mobile filters */}
        {showMobileFilters && (
          <div className="fixed inset-0 z-50 overflow-auto md:hidden" style={{backgroundColor: 'rgba(0,0,0,0.4)'}}>
            <div className="fixed bottom-0 left-0 right-0 bg-white rounded-t-xl shadow-lg overflow-hidden max-h-[85vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
                <h2 className="text-lg font-semibold">Filters</h2>
                <div className="flex items-center space-x-4">
                  <Button
                    variant="outline"
                    className="text-sm border-teal-200 text-teal-700 hover:bg-teal-50 hover:text-teal-800"
                    onClick={resetFilters}
                  >
                    Reset
                  </Button>
                  <Button
                    onClick={() => setShowMobileFilters(false)}
                    className="bg-teal-600 hover:bg-teal-700 text-white"
                  >
                    <FilterIcon className="h-4 w-4 mr-2" />
                    Filters
                    {activeFilterCount() > 0 && (
                      <span className="ml-1 w-5 h-5 rounded-full bg-white text-teal-700 text-xs flex items-center justify-center">
                        {activeFilterCount()}
                      </span>
                    )}
                  </Button>
                </div>
              </div>

              <div className="p-4 space-y-6">
                {/* Mobile main category filters */}
                <div className="border-b border-gray-100 pb-4">
                  <h3 className="font-medium mb-3">Product Type</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(filters.mainCategory).map(([category, isActive]) => (
                      <FilterButton
                        key={category}
                        isActive={isActive}
                        onClick={() => toggleFilter('mainCategory', category)}
                        className={""} // Add custom styling based on category if desired
                      >
                        {category}
                      </FilterButton>
                    ))}
                  </div>
                </div>
                
                {/* Mobile potency sliders */}
                <div className="border-b border-gray-100 pb-4">
                  <h3 className="font-medium mb-3">THC Range</h3>
                  <div className="mb-2 flex justify-between text-sm">
                    <div className="px-2 py-1 bg-teal-50 rounded-md text-teal-700 text-xs font-medium">
                      {filters.potency.thc.value[0]}%
                    </div>
                  <div className="px-2 py-1 bg-teal-50 rounded-md text-teal-700 text-xs font-medium">
                    {filters.potency.thc.value[1]}%
                  </div>
                </div>
                <Slider
                  min={filters.potency.thc.min}
                  max={filters.potency.thc.max}
                  step={1}
                  value={filters.potency.thc.value}
                  onValueChange={(value) => handlePotencyChange('thc', value as [number, number])}
                  className="mb-6"
                />

                <h3 className="font-medium mb-3">CBD Range</h3>
                <div className="mb-2 flex justify-between text-sm">
                  <div className="px-2 py-1 bg-blue-50 rounded-md text-blue-700 text-xs font-medium">
                    {filters.potency.cbd.value[0]}%
                  </div>
                  <div className="px-2 py-1 bg-blue-50 rounded-md text-blue-700 text-xs font-medium">
                    {filters.potency.cbd.value[1]}%
                  </div>
                </div>
                <Slider
                  min={filters.potency.cbd.min}
                  max={filters.potency.cbd.max}
                  step={1}
                  value={filters.potency.cbd.value}
                  onValueChange={(value) => handlePotencyChange('cbd', value as [number, number])}
                  className="mb-6"
                />
              </div>

              {/* Mobile subcategory filters */}
              {Object.keys(filters.subcategories).length > 0 && (
                <div className="border-b border-gray-100 pb-4">
                  <h3 className="font-medium mb-3">Types</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(filters.subcategories).map(([subcategory, isActive]) => (
                      <FilterButton
                        key={subcategory}
                        isActive={isActive}
                        onClick={() => toggleFilter('subcategories', subcategory)}
                        className={subcategory === 'Full Spectrum' ? 'bg-teal-100 text-teal-800 border-teal-200' : ''}
                      >
                        {subcategory}
                      </FilterButton>
                    ))}
                  </div>
                </div>
              )}

              {/* Mobile weight filters */}
              <div className="border-b border-gray-100 pb-4">
                <h3 className="font-medium mb-3">Weight</h3>
                <div className="grid grid-cols-2 gap-2">
                  {getSortedWeightKeys().map((weight) => (
                    <FilterButton
                      key={weight}
                      isActive={filters.weights[weight]}
                      onClick={() => toggleFilter('weights', weight)}
                    >
                      {weight}
                    </FilterButton>
                  ))}
                </div>
              </div>

              {/* Mobile strain type filters */}
              <div className="border-b border-gray-100 pb-4">
                <h3 className="font-medium mb-3">Strain Type</h3>
                <div className="grid grid-cols-2 gap-2">
                  {Object.keys(filters.strainTypes)
                    .sort((a, b) => {
                      // Custom sort order for strain types
                      const order: Record<string, number> = {
                        'Sativa': 1,
                        'Sativa Hybrid': 2,
                        'Hybrid': 3,
                        'Indica Hybrid': 4,
                        'Indica': 5
                      };
                      return (order[a] || 99) - (order[b] || 99);
                    })
                    .map((type) => (
                      <FilterButton
                        key={type}
                        isActive={filters.strainTypes[type]}
                        onClick={() => toggleFilter('strainTypes', type)}
                      >
                        {type}
                      </FilterButton>
                    ))
                  }
                </div>
              </div>

              {/* Mobile effects filters */}
              <div className="border-b border-gray-100 pb-4">
                <h3 className="font-medium mb-3">Effects</h3>
                <div className="grid grid-cols-2 gap-2">
                  {Object.keys(filters.effects).map((effect) => (
                    <FilterButton
                      key={effect}
                      isActive={filters.effects[effect]}
                      onClick={() => toggleFilter('effects', effect)}
                    >
                      {effect}
                    </FilterButton>
                  ))}
                </div>
              </div>

              {/* Mobile brand filters */}
              <div className="border-b border-gray-100 pb-4">
                <h3 className="font-medium mb-3">Brands</h3>
                <div className="grid grid-cols-2 gap-2">
                  {Object.keys(filters.brands).map((brand) => (
                    <FilterButton
                      key={brand}
                      isActive={filters.brands[brand]}
                      onClick={() => toggleFilter('brands', brand)}
                    >
                      {brand}
                    </FilterButton>
                  ))}
                </div>
              </div>

              {/* Mobile sorting */}
              <div className="pb-4">
                <h3 className="font-medium mb-3">Sort By</h3>
                <select
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                  className="w-full border border-gray-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-transparent"
                >
                  <option value="featured">Featured</option>
                  <option value="price-asc">Price: Low to High</option>
                  <option value="price-desc">Price: High to Low</option>
                  <option value="potency-desc">THC %: High to Low</option>
                  <option value="name-asc">Name: A to Z</option>
                </select>
              </div>
            </div>
          </div>
        </div>
        )}

        <div className="flex flex-col md:flex-row gap-8">
          {/* Desktop filters sidebar */}
          <div className="hidden md:block w-72 flex-shrink-0">
            <div className="bg-white rounded-xl border border-gray-100 shadow-md overflow-hidden">
              <div className="p-4 bg-gradient-to-r from-amber-50 to-amber-100 flex items-center justify-between">
                <div className="flex items-center">
                  <FilterIcon className="h-4 w-4 mr-2 text-amber-600" />
                  <h2 className="font-medium text-amber-800">Refine Your Search</h2>
                </div>
                {activeFilterCount() > 0 && (
                  <span className="inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-600 text-white">
                    {activeFilterCount()}
                  </span>
                )}
              </div>

              <div className="p-4">
                <Button
                  variant="outline"
                  onClick={resetFilters}
                  className="w-full mb-6 bg-white text-teal-700 border-teal-200 hover:bg-teal-50 hover:text-teal-800 transition-all duration-200 shadow-sm rounded-lg py-2.5"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                    <path d="M2 12a10 10 0 0 1 10-10v0a10 10 0 0 1 10 10v0a10 10 0 0 1-10 10v0A10 10 0 0 1 2 12v0Z"/>
                    <path d="m14 16-4-4 4-4"/>
                  </svg>
                  Reset All Filters
                </Button>

                {/* Active Filters */}
                <div className="space-y-2 mb-4">
                  {Object.entries(filters).flatMap(([category, values]) => {
                    if (category === 'potency') return [];

                    return Object.entries(values as Record<string, boolean>)
                      .filter(([_, isActive]) => isActive)
                      .map(([value]) => (
                        <Badge
                          key={`${category}-${value}`}
                          variant="outline"
                          className="mr-1 mb-1 border-amber-500 text-amber-700 hover:bg-amber-50"
                        >
                          {value}
                          <X
                            className="ml-1 h-3 w-3 cursor-pointer"
                            onClick={() => toggleFilter(category as any, value)}
                          />
                        </Badge>
                      ));
                  })}
                </div>

                {/* Subcategory filters moved to accordion section */}

                {/* Potency sliders */}
                {/* THC Potency section */}
                <div className="px-1 mb-8">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-medium text-gray-700">Strength</h3>
                    <div className="px-2 py-1 bg-teal-50 rounded-md text-teal-700 text-xs font-medium">
                      {filters.potency.thc.value[0]}% - {filters.potency.thc.value[1]}%
                    </div>
                  </div>

                  <Slider
                    min={filters.potency.thc.min}
                    max={filters.potency.thc.max}
                    step={1}
                    value={filters.potency.thc.value}
                    onValueChange={(value) => handlePotencyChange('thc', value as [number, number])}
                    className="mb-8"
                  />

                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-medium text-gray-700">CBD Level</h3>
                    <div className="px-2 py-1 bg-amber-50 rounded-md text-amber-700 text-xs font-medium">
                      {filters.potency.cbd.value[0]}% - {filters.potency.cbd.value[1]}%
                    </div>
                  </div>
                  <Slider
                    min={filters.potency.cbd.min}
                    max={filters.potency.cbd.max}
                    step={1}
                    value={filters.potency.cbd.value}
                    onValueChange={(value) => handlePotencyChange('cbd', value as [number, number])}
                    className="mb-2"
                  />
                </div>

                {/* Accordion filters for desktop */}
                <Accordion type="multiple" className="w-full" defaultValue={['main-category', 'subcategories', 'effects']}>
                  {/* Main Category filter */}
                  <AccordionItem value="main-category" className="border-b border-gray-100">
                    <AccordionTrigger className="text-sm font-medium text-gray-700 hover:text-amber-700 py-3">
                      <div className="flex items-center">
                        Product Type
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="grid grid-cols-2 gap-2 pt-2">
                        {Object.entries(filters.mainCategory).map(([category, isActive]) => (
                          <FilterButton
                            key={category}
                            isActive={isActive}
                            onClick={() => toggleFilter('mainCategory', category)}
                          >
                            {category}
                          </FilterButton>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                  
                  {/* Weight filter */}
                  <AccordionItem value="weights" className="border-b border-gray-100">
                    <AccordionTrigger className="text-sm font-medium text-gray-700 hover:text-amber-700 py-3">
                      <div className="flex items-center">
                        Weight
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="grid grid-cols-2 gap-2 pt-2">
                        {getSortedWeightKeys().map((weight) => (
                          <FilterButton
                            key={weight}
                            isActive={filters.weights[weight]}
                            onClick={() => toggleFilter('weights', weight)}
                          >
                            {weight}
                          </FilterButton>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  {/* Subcategory filter */}
                  {Object.keys(filters.subcategories).length > 0 && (
                    <AccordionItem value="subcategories" className="border-b border-gray-100">
                      <AccordionTrigger className="text-sm font-medium text-gray-700 hover:text-amber-700 py-3">
                        <div className="flex items-center">
                          Types
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="grid grid-cols-2 gap-2 pt-2">
                          {Object.entries(filters.subcategories).map(([subcategory, isActive]) => (
                            <FilterButton
                              key={subcategory}
                              isActive={isActive}
                              onClick={() => toggleFilter('subcategories', subcategory)}
                            >
                              {subcategory}
                            </FilterButton>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  )}

                  {/* Strain Type filter */}
                  <AccordionItem value="strain-type" className="border-b border-gray-100">
                    <AccordionTrigger className="text-sm font-medium text-gray-700 hover:text-amber-700 py-3">
                      <div className="flex items-center">
                        Strain Type
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="grid grid-cols-2 gap-2 pt-2">
                        {Object.keys(filters.strainTypes)
                          .sort((a, b) => {
                            // Custom sort order for strain types
                            const order: Record<string, number> = {
                              'Sativa': 1,
                              'Sativa Hybrid': 2,
                              'Hybrid': 3,
                              'Indica Hybrid': 4,
                              'Indica': 5
                            };
                            return (order[a] || 99) - (order[b] || 99);
                          })
                          .map((type) => (
                            <FilterButton
                              key={type}
                              isActive={filters.strainTypes[type]}
                              onClick={() => toggleFilter('strainTypes', type)}
                            >
                              {type}
                            </FilterButton>
                          ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  {/* Effects filter */}
                  <AccordionItem value="effects" className="border-b border-gray-100">
                    <AccordionTrigger className="text-sm font-medium text-gray-700 hover:text-amber-700 py-3">
                      <div className="flex items-center">
                        Effects
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="grid grid-cols-2 gap-2 pt-2">
                        {Object.keys(filters.effects).map((effect) => (
                          <FilterButton
                            key={effect}
                            isActive={filters.effects[effect]}
                            onClick={() => toggleFilter('effects', effect)}
                          >
                            {effect}
                          </FilterButton>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  {/* Brands filter */}
                  <AccordionItem value="brands" className="border-b border-gray-100">
                    <AccordionTrigger className="text-sm font-medium text-gray-700 hover:text-amber-700 py-3">
                      <div className="flex items-center">
                        Brands
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="grid grid-cols-2 gap-2 pt-2">
                        {Object.keys(filters.brands).map((brand) => (
                          <FilterButton
                            key={brand}
                            isActive={filters.brands[brand]}
                            onClick={() => toggleFilter('brands', brand)}
                          >
                            {brand}
                          </FilterButton>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            </div>
          </div>

          {/* Product grid */}
          <div className="flex-1">
            {/* Active filters display */}
            {activeFilterCount() > 0 && (
              <div className="mb-4 flex flex-wrap gap-2">
                {Object.entries(filters.weights)
                  .filter(([_, isSelected]) => isSelected)
                  .map(([weight]) => (
                    <div
                      key={`weight-${weight}`}
                      className="px-2 py-1 bg-amber-600 text-white text-xs font-medium rounded-md flex items-center"
                    >
                      {weight}
                      <button
                        className="ml-1.5 text-white/80 hover:text-white"
                        onClick={() => toggleFilter('weights', weight)}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}

                {Object.entries(filters.strainTypes)
                  .filter(([_, isSelected]) => isSelected)
                  .map(([type]) => (
                    <div
                      key={`type-${type}`}
                      className="px-2 py-1 bg-amber-600 text-white text-xs font-medium rounded-md flex items-center"
                    >
                      {type}
                      <button
                        className="ml-1.5 text-white/80 hover:text-white"
                        onClick={() => toggleFilter('strainTypes', type)}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}

                {Object.entries(filters.effects)
                  .filter(([_, isSelected]) => isSelected)
                  .map(([effect]) => (
                    <div
                      key={`effect-${effect}`}
                      className="px-2 py-1 bg-amber-600 text-white text-xs font-medium rounded-md flex items-center"
                    >
                      {effect}
                      <button
                        className="ml-1.5 text-white/80 hover:text-white"
                        onClick={() => toggleFilter('effects', effect)}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}

                {Object.entries(filters.brands)
                  .filter(([_, isSelected]) => isSelected)
                  .map(([brand]) => (
                    <div
                      key={`brand-${brand}`}
                      className="px-2 py-1 bg-amber-600 text-white text-xs font-medium rounded-md flex items-center"
                    >
                      {brand}
                      <button
                        className="ml-1.5 text-white/80 hover:text-white"
                        onClick={() => toggleFilter('brands', brand)}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}

                {filters.potency.thc.isAdjusted && (
                  <div
                    className="px-2 py-1 bg-amber-600 text-white text-xs font-medium rounded-md flex items-center"
                  >
                    THC: {filters.potency.thc.value[0]}% - {filters.potency.thc.value[1]}%
                    <button
                      className="ml-1.5 text-white/80 hover:text-white"
                      onClick={() => {
                        setFilters(prev => ({
                          ...prev,
                          potency: {
                            ...prev.potency,
                            thc: {
                              ...prev.potency.thc,
                              value: [prev.potency.thc.min, prev.potency.thc.max],
                              isAdjusted: false
                            }
                          }
                        }));
                      }}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                )}

                {filters.potency.cbd.isAdjusted && (
                  <div
                    className="px-2 py-1 bg-amber-600 text-white text-xs font-medium rounded-md flex items-center"
                  >
                    CBD: {filters.potency.cbd.value[0]}% - {filters.potency.cbd.value[1]}%
                    <button
                      className="ml-1.5 text-white/80 hover:text-white"
                      onClick={() => {
                        setFilters(prev => ({
                          ...prev,
                          potency: {
                            ...prev.potency,
                            cbd: {
                              ...prev.potency.cbd,
                              value: [prev.potency.cbd.min, prev.potency.cbd.max],
                              isAdjusted: false
                            }
                          }
                        }));
                      }}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Results count */}
            <div className="mb-4 text-sm text-gray-500">
              {filteredProducts.length} {filteredProducts.length === 1 ? 'Product' : 'Products'}
            </div>

            {loading ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 sm:gap-3">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="bg-gray-200 aspect-square rounded-xl mb-3"></div>
                    <div className="h-4 bg-gray-200 rounded mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  </div>
                ))}
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="text-center py-12">
                <h3 className="text-lg font-medium text-gray-900 mb-2">No products found</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Try adjusting your filters or search query
                </p>
                <Button variant="outline" onClick={resetFilters} className="border-teal-200 text-teal-700 hover:bg-teal-50 hover:text-teal-800">Reset</Button>
              </div>
            ) : (
              <>
                <div className="mb-8 flex justify-between items-center">
                  <h2 className="text-xl font-bold">Products ({filteredProducts.length})</h2>
                </div>
                
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 sm:gap-3 mb-8">
                  {filteredProducts.length === 0 ? (
                    <p className="col-span-full text-center text-gray-500 my-8">
                      No topicals found. Try adjusting your filters.
                    </p>
                  ) : (
                    filteredProducts
                    .slice((currentPage - 1) * productsPerPage, currentPage * productsPerPage)
                    .map((product) => (
                      <ProductCard key={product.id} product={product} buttonClassName="bg-teal-600 hover:bg-teal-700" />
                    ))
                  )}
                </div>

                {/* Pagination controls */}
                {filteredProducts.length > productsPerPage && (
                  <div className="flex justify-center mt-8">
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className="border-teal-200 text-teal-700 hover:bg-teal-50 hover:text-teal-800 disabled:opacity-50"
                      >
                        <ChevronLeft className="h-4 w-4 mr-1" />
                        Previous
                      </Button>

                      <div className="text-sm text-gray-500">
                        Page {currentPage} of {Math.ceil(filteredProducts.length / productsPerPage)}
                      </div>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(filteredProducts.length / productsPerPage)))}
                        disabled={currentPage >= Math.ceil(filteredProducts.length / productsPerPage)}
                        className="border-teal-200 text-teal-700 hover:bg-teal-50 hover:text-teal-800 disabled:opacity-50"
                      >
                        Next
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
